import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type {
  SellerDashboardAnalytics,
  SellerDashboardRange,
} from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";
import type { UseDashboardAnalyticsQueryParams } from "@/modules/dashboard/types/dashboard-analytics.types";

function buildPath(range: SellerDashboardRange): string {
  const query = new URLSearchParams({ range });
  return `${endpoints.seller.analyticsDashboard}?${query.toString()}`;
}

/**
 * SQL-aggregated dashboard metrics for the selected range. The whole KPI row,
 * the sales/profit chart and the top-products list read from this one response
 * — the page no longer buckets orders in JavaScript.
 *
 * `range` is part of the query key, so changing it refetches. `keepPreviousData`
 * holds the last range's numbers on screen while the new ones load, so flipping
 * 7d → 30d doesn't blank the page back to a skeleton.
 */
export function useDashboardAnalyticsQuery({
  storeSlug,
  range,
}: UseDashboardAnalyticsQueryParams) {
  const query = useQuery({
    queryKey: queryKeys.seller.dashboardAnalytics(storeSlug, range),
    queryFn: (): Promise<SellerDashboardAnalytics> =>
      apiJsonClient<SellerDashboardAnalytics>(buildPath(range)),
    enabled: storeSlug.length > 0,
    placeholderData: keepPreviousData,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    /** True during any fetch, including the background one after a range flip. */
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}
